import { TrackedData } from './trackedData';
import { AssertionException } from '../../../../../utils/log';

/** Gets tracking data supplied by the iOS app. */
export const getTrackingData = (): TrackedData => {
  if (!window.trackedData) {
    throw AssertionException('Object containing tracking data is missing. We cannot continue as it is required.');
  }

  return mapToTrackedData(window.trackedData);
};

/** Maps data supplied by iOS to domain object. */
export const mapToTrackedData = (data: iOSTrackedData): TrackedData => {
  return new TrackedData(
    data.timeSpentInCourse ? data.timeSpentInCourse : 0,
    data.log ? data.log : '',
    data.startedCourseAt ? data.startedCourseAt * 1000 : null,
    data.lastAccessedTime ? data.lastAccessedTime * 1000 : null,
    data.completedCourseAt ? data.completedCourseAt * 1000 : null
  );
};

/** Maps data from domain object to iOS object. */
export const mapToTrackedDataForiOS = (trackedData: TrackedData): iOSTrackedData => {
  const startedCourseAt = trackedData.getStartedCourseAt();
  const lastAccessedTime = trackedData.getLastAccessedTimeAt();
  const completedCourseAt = trackedData.getCompletedCourseAt();
  return {
    log: trackedData.getLog(),
    timeSpentInCourse: trackedData.getTimeSpentInCourse(),
    startedCourseAt: startedCourseAt ? Math.floor(startedCourseAt / 1000) : null,
    lastAccessedTime: lastAccessedTime ? Math.floor(lastAccessedTime / 1000) : null,
    completedCourseAt: completedCourseAt ? Math.floor(completedCourseAt / 1000) : null,
  };
};

/** Declares a function type for notifying iOS about the change in tracked data.  */
export type iOSNotificationFunction = (changedData: TrackedData) => void;

/** Notifies an iOS app about the change in tracked data. */
export const notifyMobileAppTrackedDataChanged: iOSNotificationFunction = (changedData: TrackedData): void => {
  const webKitToMessageTo = window.webkit;
  if (!webKitToMessageTo) {
    throw Error('WebKit is not available here, tracking client is probably used in the incorrect context.');
  }

  const data: iOSTrackedData = mapToTrackedDataForiOS(changedData);

  // Name of the handler cannot be changed as it is constant agreed upon with the iOS mobile app
  webKitToMessageTo.messageHandlers.aelAeroProgressTrackingMessageHandler.postMessage(data);
};

/** Tracking data passed by the iOS app to the application. */
export type iOSTrackedData = {
  /**
   * Time student spent in the course in milliseconds.
   */
  timeSpentInCourse?: number;
  /**
   * Contains all items student has progressed through.
   */
  log?: string;
  /**
   * Denotes a time when student opened the course for the first time represented by UNIX time-stamp thus elapsed seconds since January 1st 1970 00:00:00 UTC.
   * When null student has not started the course.
   */
  startedCourseAt?: number | null;
  /**
   * Denotes a time when student made the last progress in the course represented by UNIX time-stamp thus elapsed seconds since January 1st 1970 00:00:00 UTC.
   * When null student has not started the course.
   */
  lastAccessedTime?: number | null;
  /**
   * Denotes a time when student has completed the course represented by UNIX time-stamp thus elapsed seconds since January 1st 1970 00:00:00 UTC.
   * When null student has not completed the course.
   */
  completedCourseAt?: number | null;
};
